import * as React from 'react'
import clsx from 'clsx'
import { SxProps, sx as sxFunc, TypographySxProps } from '../sx'
import style from './index.module.scss'
import SemanticInformation from '../internal/svg-icons/SemanticInformation'
import { capitalize } from '../../utils'

export interface AvatarClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if not `src` or `srcSet`. */
  colorDefault: string
  /** Styles applied to the root element if `variant="circular"`. */
  variantCircular: string
  /** Styles applied to the root element if `variant="rounded"`. */
  variantRounded: string
  /** Styles applied to the root element if `variant="square"`. */
  variantSquare: string
  /** Styles applied to the img element if either `src` or `srcSet` is defined. */
  img: string
  /** Styles applied to the fallback icon */
  fallback: string
}

export interface AvatarProps
  extends Partial<React.HTMLAttributes<HTMLDivElement>> {
  /**
   * Used in combination with `src` or `srcSet` to
   * provide an alt attribute for the rendered `img` element.
   */
  alt?: string
  /**
   * Used to render icon or text elements inside the Avatar if `src` is not set.
   * This can be an element, or just a string.
   */
  children?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<AvatarClasses>
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   * @default div
   */
  Component?: React.ElementType
  /**
   * [Attributes](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img#attributes) applied to the `img` element if the component is used to display an image.
   * It can be used to listen for the loading error event.
   */
  imgProps?: React.ImgHTMLAttributes<HTMLImageElement> & {
    sx?: SxProps
  }
  /**
   * The `size` attribute for avatar.
   * deprecated value : 'exSmall' | 'small' | 'medium' | 'large' | 'exLarge'
   * exSmall : 24
   * small : 32
   * medium : 48
   * large : 64
   * exLarge : 80
   * @default 24
   */
  size?:
    | 'exSmall'
    | 'small'
    | 'medium'
    | 'large'
    | 'exLarge'
    | Exclude<TypographySxProps['fontSize'], 12 | 14 | 'inherit'>
  /**
   * The color of the component.
   * @default 'secondary'
   */
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  /**
   * The `src` attribute for the `img` element.
   */
  src?: string
  /**
   * The `srcSet` attribute for the `img` element.
   * Use this attribute for responsive image display.
   */
  srcSet?: string
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The shape of the avatar.
   * @default 'circular'
   */
  variant?: 'circular' | 'rounded' | 'square'
  /**
   * Display full alt text, use it when you want to diplay 2 of more characters
   * @default false
   */
  enableDisplayFullAlt?: boolean
}

const sizeMapping = {
  exSmall: 24,
  small: 32,
  medium: 48,
  large: 64,
  exLarge: 80,
}

function useLoaded({
  crossOrigin,
  referrerPolicy,
  src,
  srcSet,
}: React.ImgHTMLAttributes<HTMLImageElement>) {
  const [loaded, setLoaded] = React.useState<false | 'error' | 'loaded'>(false)

  React.useEffect(() => {
    if (!src && !srcSet) {
      return undefined
    }

    setLoaded(false)

    let active = true
    const image = new Image()
    image.onload = () => {
      if (!active) {
        return
      }
      setLoaded('loaded')
    }
    image.onerror = () => {
      if (!active) {
        return
      }
      setLoaded('error')
    }
    image.crossOrigin = crossOrigin!
    image.referrerPolicy = referrerPolicy!
    image.src = src!
    if (srcSet) {
      image.srcset = srcSet!
    }

    return () => {
      active = false
    }
  }, [crossOrigin, referrerPolicy, src, srcSet])

  return loaded
}

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  function Avatar(props, ref) {
    const {
      alt,
      children: childrenProp,
      className,
      classes,
      Component = 'div',
      imgProps,
      size: sizeProp = 24,
      color = 'secondary',
      src,
      srcSet,
      sx,
      variant = 'circular',
      enableDisplayFullAlt = false,
      ...other
    } = props

    let children = null

    // Use a hook instead of onError on the img element to support server-side rendering.
    // @ts-ignore

    const loaded = useLoaded({ ...imgProps, src, srcSet })
    const hasImg = src || srcSet
    const isImgNotFailing = hasImg && loaded !== 'error'

    if (isImgNotFailing) {
      children = (
        <img
          alt={alt}
          src={src}
          srcSet={srcSet}
          className={clsx(style.avatarImg, classes?.img, sxFunc(imgProps?.sx))}
          {...imgProps}
        />
      )
    } else if (childrenProp != null) {
      children = childrenProp
    } else if (hasImg && alt) {
      children = enableDisplayFullAlt ? alt : alt[0] // The first character of alt text
    } else {
      children = (
        <SemanticInformation
          className={clsx(style.avatarFallback, classes?.fallback)}
        />
      )
    }

    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line
      React.useEffect(() => {
        if (typeof sizeProp === 'string') {
          console.warn(
            'Use numeric size instead, for setting the size of Avatar.\n' +
              'exSmall : 24 \n' +
              'small : 32 \n' +
              'medium : 48 \n' +
              'large : 64 \n' +
              'exLarge : 80 \n'
          )
        }
      }, [sizeProp])
    }

    const size = React.useMemo(() => {
      if (typeof sizeProp !== 'string') {
        return sizeProp
      } else if (typeof sizeProp === 'string') {
        return sizeMapping[sizeProp] ?? sizeProp
      }
      return sizeProp
    }, [sizeProp])

    const _classes = clsx(
      style.root,
      classes?.root,
      // when image loading fails (confusing name, please be careful editing condition)
      !isImgNotFailing && [style.imgLoadFailed, classes?.colorDefault],
      style[`variant-${variant}`],
      style[`size-${size}`],
      style[`color-${color}`],
      // @ts-ignore
      classes?.[`variant${capitalize(variant)}`],
      className,
      sxFunc(sx)
    )

    return (
      <Component className={_classes} ref={ref} {...other}>
        {children}
      </Component>
    )
  }
)
